import { emitNotificationsChanged } from "./notifications-events";
import { emitMessagesChanged } from "./messages-events";
import { RateLimitError, parseRetryAfter, buildRateLimitMessage } from "./api-error";
import { fetchWithTransientRetry } from "./transient-fetch";
import { getSharedRefreshedToken, forceSessionExpiredRedirect } from "./auth-refresh";
import { getTurnstileToken, TURNSTILE_HEADER } from "./turnstile";

// Re-exported for back-compat: `RateLimitError` was originally defined
// here. It now lives in the shared `api-error` module so both
// `users-api.ts` and `bookings-api.ts` throw the identical type.
export { RateLimitError } from "./api-error";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

function getImpersonationHeaders(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem("bac_impersonation");
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (data.expiresAt < Date.now()) return {};
    if (!data.userId || !data.sessionId) return {};
    return {
      "X-Impersonate-User-Id": data.userId,
      "X-Impersonation-Session-Id": data.sessionId,
    };
  } catch {
    return {};
  }
}

function authHeaders(token: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...getImpersonationHeaders(),
  };
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;
  constructor(message: string, status: number, body?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    if (body && typeof body.code === "string") this.code = body.code;
    this.details = body;
  }
}

/**
 * True when an error is the expected "incomplete-profile" 404 from an authed
 * endpoint: the user has a valid Supabase session but no `public.users` row yet
 * (verified email/OAuth, hasn't finished the name + Terms completion step). The
 * auth middleware deliberately no longer auto-creates the row (anti-ghost-
 * account), so global authed calls (header badge counts, effective-host-status)
 * respond 404 `{ error: "User profile not found" }`. Callers treat this as a
 * neutral empty state (0 counts / not a host) instead of throwing or reporting
 * it to Sentry — the profile-completion flow already surfaces the real state to
 * the user. Genuinely unexpected failures are NOT matched here.
 */
export function isMissingProfileError(err: unknown): boolean {
  const e = err as { status?: number; message?: string } | undefined;
  return e?.status === 404 && e?.message === "User profile not found";
}

// Task #4296 — shape of the `moderation` payload the PATCH /users/me/settings
// gate returns on a 422 when profile free-text is blocked.
export interface ProfileModerationBlock {
  message: string;
  escalated: boolean;
  attemptsRemaining: number | null;
}

/**
 * Task #4296 — pull the synchronous profile-moderation advice out of a thrown
 * error. The server returns `{ error: "profile_content_blocked", moderation: {
 * blocked, escalated, i18nKey, message, attemptsRemaining, ... } }` with a 422.
 * We prefer the localized copy keyed by `i18nKey`; the server's English
 * `message` is the fallback so older locales still render something sensible.
 * Returns null when the error isn't a moderation block.
 */
export function parseModerationError(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): ProfileModerationBlock | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const details = err.details as { error?: string; moderation?: Record<string, unknown> } | undefined;
  const mod = details?.moderation;
  if (!mod || details?.error !== "profile_content_blocked") return null;
  const i18nKey = typeof mod.i18nKey === "string" ? mod.i18nKey : null;
  const serverMessage = typeof mod.message === "string" ? mod.message : "";
  // i18next returns the key itself when a string is missing; fall back to the
  // server copy in that case so we never show a raw dotted key.
  const localized = i18nKey ? t(i18nKey) : "";
  const message = localized && localized !== i18nKey ? localized : serverMessage;
  return {
    message,
    escalated: mod.escalated === true,
    attemptsRemaining:
      typeof mod.attemptsRemaining === "number" ? mod.attemptsRemaining : null,
  };
}

async function authFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  // Task #5799 — retry idempotent (GET) reads on a transient 5xx / network
  // blip before surfacing an error; writes (init.method POST/PUT/PATCH/
  // DELETE) carry a mutating method and are issued exactly once.
  const res = await fetchWithTransientRetry(`${API_BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(token), ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 429) {
      // Prefer the server's own friendly copy; otherwise build a
      // seconds-aware message so the UI tells the user exactly how long to
      // wait ("please wait ~N seconds") instead of a vague "a few minutes".
      // This is a handled, non-fatal condition — Sentry drops it via the
      // `RateLimitError` filter in lib/sentry.ts (Task #3595).
      const retryAfter = parseRetryAfter(res, body);
      throw new RateLimitError(buildRateLimitMessage(body, retryAfter), retryAfter);
    }
    if (res.status === 401) {
      // Coalesces with concurrent 401s in bookings-api / listings-api /
      // subscriptions-api — only one `refreshSession` call fires regardless
      // of how many dashboard sections hit an expired token at the same time.
      const freshToken = await getSharedRefreshedToken();
      if (freshToken && freshToken !== token) {
        const retryRes = await fetchWithTransientRetry(`${API_BASE}${path}`, {
          ...init,
          headers: { ...authHeaders(freshToken), ...init?.headers },
        });
        if (retryRes.ok) return retryRes.json();
        const retryBody = await retryRes.json().catch(() => ({}));
        if (retryRes.status === 401 || retryBody.error === "session_expired") {
          forceSessionExpiredRedirect();
        }
        throw new ApiError(
          retryBody.error || `API error ${retryRes.status}`,
          retryRes.status,
          retryBody,
        );
      }
      // No new token — session is truly dead.
      forceSessionExpiredRedirect();
      // Fall through so callers don't receive undefined.
    }
    throw new ApiError(body.error || `API error ${res.status}`, res.status, body);
  }
  return res.json();
}

/**
 * Read the *effective* host status for the current request. When called with
 * active impersonation headers (automatically attached by `authHeaders`), the
 * server resolves host status against the impersonated user, so the admin can
 * see the impersonated host's host-gated UI. Without impersonation it returns
 * the caller's own host status. Derived from owned cars by `users.id`, so it
 * works for legacy targets with no linked auth account.
 */
export async function fetchEffectiveHostStatus(
  token: string,
): Promise<{ user_id: string; is_host: boolean; impersonating: boolean }> {
  return authFetch<{ user_id: string; is_host: boolean; impersonating: boolean }>(
    `/auth/effective-host-status`,
    token,
  );
}

/**
 * Hide a conversation from the CURRENT user's own inbox list. This does
 * NOT delete any message history and does NOT affect the other
 * participant. The thread auto-reappears (server-derived) once a newer
 * message arrives, so there is intentionally no "unhide" call.
 */
export async function hideConversation(publicId: string, token: string): Promise<{ ok: boolean }> {
  return authFetch<{ ok: boolean }>(
    `/messages/conversations/${encodeURIComponent(publicId)}/hide`,
    token,
    { method: "POST" },
  );
}

export interface Conversation {
  id: number;
  public_id: string;
  car_id: number | null;
  user_1_id: string;
  user_2_id: string;
  last_message_at: string;
  other_user: { id: string; first_name: string; last_name: string; profile_image: string | null } | null;
  last_message: { content: string; created_at: string; sender_id: string; is_system: boolean; system_type?: string | null } | null;
  unread_count: number;
  car: { id: number; make: string; model: string; year: number } | null;
  /**
   * Task #3096 — when the conversation is scoped to a specific trip
   * (host accepts a request, instant-booking webhook fires, or the
   * renter/host opens the thread from a trip page), the server
   * surfaces a compact trip payload here so the inbox can render the
   * booking-date subtitle ("Dec 5 → Dec 7") instead of "Enquiry".
   * `null` for legacy enquiry-only threads.
   */
  trip: {
    id: number;
    public_id: string;
    status: string;
    started_at: string;
    drop_off: string;
    times_captured?: number | null;
    isOwner: boolean;
  } | null;
}

export interface Message {
  id: number | string;
  conversation_id: number;
  sender_id: string | null;
  /**
   * Optional addressee for system / platform notification messages. When set,
   * the read-side filters out the message for any user other than this id so
   * a host doesn't see "review your renter" prompts addressed to the renter
   * (and vice versa).
   */
  recipient_id?: string | null;
  content: string;
  is_system: boolean;
  is_read: boolean;
  /** When the recipient first read this message — drives web read receipts. */
  read_at?: string | null;
  /**
   * Task #6586 — client-generated idempotency key (uuid) for web sends.
   * Present on optimistic bubbles and echoed back by the server; used to
   * key the per-send delivery state (sending / failed / retrying).
   */
  client_message_id?: string | null;
  /**
   * Task #6587 — image attachment. `attachment_url` is a CDN URL from our
   * own storage bucket (never derived from message content) and
   * `attachment_type` is 'image' for MVP. Both null/absent on text rows.
   */
  attachment_url?: string | null;
  attachment_type?: string | null;
  is_filtered: boolean;
  /**
   * Tag identifying a structured "system" payload (e.g. `trip_proposal`).
   * The chat client only renders rich cards when this is set — the
   * message body itself is never parsed for magic prefixes.
   */
  system_type?: string | null;
  /** Structured payload that backs the rich card. Treat as untrusted JSON. */
  system_payload?: Record<string, unknown> | null;
  /**
   * Task #3032 — server-resolved "previous pricing" snapshot for
   * `trip_proposal` system messages. Carries the owner_earn /
   * payment_due that were in effect immediately BEFORE this
   * suggestion was created, derived from the prior non-superseded
   * `trip_suggestions` row (falling back to the trip row's pricing
   * when no prior row exists). Used by ProposalCard to render the
   * correct historical delta on terminal-state cards (accepted /
   * declined / expired / withdrawn / superseded) — the live
   * `active_trip.*` already equals the accepted suggestion, so
   * without this baseline the host card collapses to "no change".
   * Pending cards intentionally ignore this and continue to compare
   * against the live trip pricing.
   */
  baseline?: { owner_earn: number; payment_due: number } | null;
  created_at: string;
  twilio_message_sid?: string | null;
}

export interface ConversationDetail {
  id: number;
  public_id: string;
  car_id: number | null;
  user_1_id: string;
  user_2_id: string;
  sid: string | null;
  other_user: {
    id: string;
    first_name: string;
    last_name: string;
    profile_image: string | null;
    created_at?: string;
    // Task #3212 — present only when the booking is confirmed-and-beyond
    // (the server withholds it otherwise). Name + phone only, never email.
    phone?: string | null;
  } | null;
  car: {
    id: number;
    make: string;
    model: string;
    year: number;
    slug: string;
    city?: string;
    state?: string;
    price_driving?: number;
    description?: string;
    primary_image?: string | null;
    // Task #975: needed by the suggest-a-change location editor when the
    // chat happens to be the entry point for a proposal.
    latitude?: number | null;
    longitude?: number | null;
    country_code?: string | null;
  } | null;
  // Task #4253 — authoritative "is the viewer the car owner" signal, derived
  // server-side from the car's owner_id. Used by the Details sidebar to label
  // enquiry (no-trip) threads correctly: a host viewing a renter's enquiry
  // sees "Renter"/"Contact renter" instead of the old falsy-default "Host".
  // `null` when the conversation has no linked car. Threads WITH an active
  // trip continue to use the authoritative `active_trip.isOwner` path.
  viewer_is_owner?: boolean | null;
  // Task #4617 — the effective viewer's numeric message-domain id, resolved
  // server-side from the impersonation-aware `auth.userId`. Under admin
  // impersonation this is the impersonated user's `users.id`, NOT the
  // admin's, so the thread renders from the impersonated user's
  // perspective. The client uses it (falling back to its own `profile.id`
  // only when absent) for bubble alignment, the optimistic-send sender id,
  // the typing-indicator self check, the past-proposal actor, and the
  // custom-quote host/renter branch.
  viewer_id?: string | null;
  active_trip: {
    id: number;
    public_id: string;
    status: string;
    started_at: string;
    pick_up: string;
    drop_off: string;
    rental_fee?: number;
    service_fee?: number;
    insurance_fee?: number;
    host_insurance_fee?: number | null;
    host_insurance_enabled?: boolean | null;
    delivery_fee?: number;
    owner_earn?: number;
    initial_request_owner_earn?: number | null;
    num_passengers?: number;
    rental_type?: string;
    event_type?: string;
    renter_id?: string;
    owner_id?: string;
    insurance_package?: number;
    insurance_package_name?: string | null;
    isOwner?: boolean;
    payment_due?: number | null;
    // Task #975: time + location current state. Lets the chat-side
    // suggest-change modal seed and pass through times/location, and
    // gives the receive side the data needed to render diffs.
    car_id?: number | null;
    is_delivery?: boolean | null;
    delivery_distance?: number | null;
    airport_code?: string | null;
    delivery_address?: string | null;
    pickup_latitude?: number | null;
    pickup_longitude?: number | null;
    times_captured?: boolean | null;
    billing_currency_code?: string | null;
    // Task #2874 — partial-payment market signals. `payment_type` is
    // either "full" (platform charges the renter for everything) or
    // "partial" (NZ today — platform only collects fees and the renter
    // pays the host directly at pickup). `platform_payment_due` is the
    // card-charged subtotal for partial trips. Both drive the chat
    // sidebar's four viewer × payment_type pricing branches.
    payment_type?: string | null;
    platform_payment_due?: number | null;
    host_direct_payment_due?: number | null;
    // Task #3025 — pricing-context fields used by the unified
    // MoneyBreakdown renter view in the chat sidebar (flat itemised
    // breakdown with the refundable surcharge shown as its own row).
    requested_hours?: number | null;
    rate_plan?: string | null;
    refundable_surcharge?: number | null;
    // Task #3025 — captured climate offset amount (billing op=6),
    // null when the renter did not buy the offset. Used so the renter
    // chat-sidebar card renders the same "Climate offset" line as
    // the trip-detail page.
    climate_offset_amount?: number | null;
  } | null;
  // Task #2717 — past-trip stub. Populated by the conversation
  // endpoint only when `active_trip` is null AND a terminal-status
  // trip (completed/cancelled/declined/expired/no_show) exists
  // between the same two users on the same car. Used to restore
  // booking context for orphaned `trip_proposal` system messages
  // and to render the "Past booking" stub in the right-hand pane.
  last_trip?: {
    id: number;
    public_id: string;
    status: string;
    started_at: string | null;
    pick_up: string | null;
    drop_off: string | null;
    owner_id: string;
    renter_id: string;
    billing_currency_code?: string | null;
    isOwner: boolean;
  } | null;
}

export function fetchConversations(token: string) {
  return authFetch<Conversation[]>("/messages/conversations?include_guest=1", token);
}

export function fetchConversation(token: string, publicId: string) {
  // Inbox feedback (May 2026): opening a conversation flips
  // `messages.is_read = true` server-side (see api-server messages.ts
  // ~L150). The header badge listens for that change over Supabase
  // realtime, but when realtime isn't connected the red "1" sticks even
  // after the thread has been read. Emit a local pub/sub event so the
  // header can refresh its unread-message count independent of realtime.
  return authFetch<ConversationDetail>(`/messages/conversations/${publicId}`, token).then((r) => {
    emitMessagesChanged();
    return r;
  });
}

export function fetchMessages(
  token: string,
  publicId: string,
  options?: { before?: string; since?: string; limit?: number },
) {
  const params = new URLSearchParams();
  if (options?.before) params.set("before", options.before);
  if (options?.since) params.set("since", options.since);
  if (options?.limit) params.set("limit", String(options.limit));
  const q = params.toString();
  return authFetch<Message[]>(
    `/messages/conversations/${publicId}/messages${q ? `?${q}` : ""}`,
    token,
  );
}

/**
 * Response shape for `/messages/send`. Either the message was
 * delivered (`message` returned) or the server blocked it for one of
 * the moderation categories and returned bespoke copy the client
 * should render as a system bubble in the thread.
 *
 * The two variants are mutually exclusive — `blocked: true` always
 * comes with `i18n_key` + `blocked_reason` and never a `message`.
 */
export type SendMessageResult =
  | { message: Message; contact_warning: boolean; blocked?: false }
  | {
      blocked: true;
      /** Single category the server chose to surface (priority order in moderation-policy.ts). */
      category:
        | "off_platform_payment"
        | "scam"
        | "abuse"
        | "contact_share"
        | "external_link"
        | "circumvention"
        | null;
      /** Every category the classifier returned. Useful for analytics, not for copy choice. */
      categories: string[];
      moderation_action: "warn" | "hard_suppress";
      /**
       * Task #6585 — true when this was the sender's 3rd-or-later
       * consecutive AI-only hard block and the attempt was held for
       * human review instead of bounced with category copy.
       */
      held_for_review?: boolean;
      /** i18n key the client should render. Falls back to `blocked_reason`. */
      i18n_key: string;
      /** English default copy — always safe to render verbatim. */
      blocked_reason: string;
      /**
       * Task #6585 — the persisted sender-only block-notice message row
       * (system_type=moderation_block_notice). When present the client
       * ingests it as a real thread row (survives refresh); when null
       * the client falls back to its legacy local-only bubble.
       */
      notice_message?: Record<string, unknown> | null;
    };

export function sendMessage(
  token: string,
  conversationPublicId: string,
  content: string,
  // Task #6586 — client-generated idempotency key (uuid). A retry of the
  // same logical send MUST reuse the same id so the server returns the
  // existing row instead of inserting a duplicate.
  clientMessageId?: string,
) {
  return authFetch<SendMessageResult>("/messages/send", token, {
    method: "POST",
    body: JSON.stringify({
      conversation_public_id: conversationPublicId,
      content,
      ...(clientMessageId ? { client_message_id: clientMessageId } : {}),
    }),
  });
}

/**
 * Task #6587 — upload a chat image attachment. Multipart FormData (not
 * authFetch, which is JSON-only): the browser sets the multipart boundary
 * itself, so we must NOT set Content-Type. Response mirrors sendMessage's
 * delivered/blocked union (a held image returns `blocked: true` with the
 * held-for-review copy + persisted notice row).
 */
export async function sendConversationImage(
  token: string,
  conversationPublicId: string,
  file: File,
): Promise<SendMessageResult> {
  const form = new FormData();
  form.append("file", file, file.name || "image");
  const res = await fetch(
    `${API_BASE}/messages/conversations/${encodeURIComponent(conversationPublicId)}/attachments`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        ...getImpersonationHeaders(),
      },
      body: form,
    },
  );
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(
      (body as { error?: string }).error || `Upload failed (${res.status})`,
      res.status,
      body as Record<string, unknown>,
    );
  }
  return body as SendMessageResult;
}

export function findOrCreateConversation(
  token: string,
  otherUserId: string,
  carId?: number,
  // Task #3096 — when the CTA is launched from a trip-aware surface
  // (trip detail, owner booking detail, post-booking confirmation),
  // pass the numeric trip id so the server promotes/creates the
  // per-trip thread rather than the enquiry slot. Listing / car-page
  // CTAs intentionally omit this so they keep hitting the enquiry
  // slot (renter pre-booking question).
  tripId?: number | null,
) {
  return authFetch<{ public_id: string; created: boolean }>("/messages/conversations/find-or-create", token, {
    method: "POST",
    body: JSON.stringify({ other_user_id: otherUserId, car_id: carId, trip_id: tripId ?? undefined }),
  });
}

/**
 * Explicitly mark a conversation's inbound messages as read and get back the
 * server read timestamp. Used to drive Supabase Realtime read receipts when a
 * message arrives while the thread is already open (the initial open is handled
 * by `fetchConversation`'s server-side mark-read side effect).
 */
export function markConversationRead(token: string, publicId: string) {
  return authFetch<{ read_at: string; count: number }>(
    `/messages/conversations/${publicId}/read`,
    token,
    { method: "POST" },
  );
}

export function fetchUnreadMessageCount(token: string) {
  return authFetch<{ count: number; latest_conversation_public_id: string | null }>(
    "/messages/unread-count?include_guest=1",
    token,
  );
}

export interface UserSettings {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  about: string | null;
  user_type: number | null;
  city: string | null;
  state: string | null;
  country_code: string | null;
  language: string | null;
  preferred_language?: string | null;
  preferred_currency: string | null;
  default_business_name: string | null;
  // Task #2814 — optional payout-themed email override (post-trip
  // earnings, payout receipts, payout-action-required). When null the
  // backend routes those emails to `email` (the login address).
  payout_email: string | null;
  // Task #2833 (Flow Review 05, Finding C) — non-null once the host
  // clicked the confirmation link sent to `payout_email`. Until then
  // `getUserPayoutEmail()` falls back to login email so a typo can't
  // silently sink payout receipts. UI shows a "Pending" badge.
  payout_email_verified_at: string | null;
  profile_image: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  renter_verified: boolean;
  notification_email_messages: boolean;
  notification_email_booking_updates: boolean;
  notification_email_rental_reminder: boolean;
  notification_email_rate_experience: boolean;
  notification_email_promotions: boolean;
  notification_sms_messages: boolean;
  notification_sms_booking_updates: boolean;
  notification_sms_rental_reminder: boolean;
  notification_push_messages: boolean;
  host_bio: string | null;
  car_story: string | null;
  auto_experience: string | null;
  specializations: string[] | null;
  certifications: string[] | null;
  cover_image: string | null;
  host_profile_moderation_status: string | null;
  host_tour_seen_at: string | null;
  earnings_tour_seen_at: string | null;
  reviews_tour_seen_at: string | null;
  created_at: string | null;
  // Headline review stats over the host's full received-review population.
  // Absent on responses served before this field shipped.
  reviewStats?: HostReviewStats | null;
  reviewStatsStatus?: "available" | "unavailable";
}

export function getUserTypeLabel(userType: number | null): string {
  switch (userType) {
    case 0: return "Individual";
    case 1: return "Professional";
    case 2: return "Enterprise";
    default: return "Individual";
  }
}

export {
  LOCKED_NOTIFICATION_PREFERENCES,
  isLockedNotificationPreference,
  type LockedNotificationPreference,
} from "@workspace/api-zod";

export function fetchUserSettings(token: string) {
  return authFetch<UserSettings>("/users/me/settings", token);
}

// Task #4296 — the success body carries a `moderation` block ONLY when the save
// escalated (3rd blocked attempt): the content is persisted HIDDEN
// (pending_manual_review) and a 200 is returned, so the 422 reject path never
// fires. The client reads this to show "sent for review" instead of "Saved".
export interface ProfileSettingsSaveResponse {
  success: boolean;
  payout_email_verification_pending?: string;
  moderation?: {
    escalated: boolean;
    i18nKey?: string;
    message?: string;
  };
}

export function updateUserSettings(token: string, updates: Partial<UserSettings>) {
  return authFetch<ProfileSettingsSaveResponse>("/users/me/settings", token, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

/**
 * Task #4296 — derive the moderation advice from a SUCCESSFUL save response.
 * Escalation returns a 200 with `moderation.escalated === true` (the reject path
 * uses 422 + parseModerationError). Mirrors parseModerationError's i18n fallback:
 * prefer the localized copy keyed by `i18nKey`, fall back to the server message.
 * Returns null when the save did not escalate.
 */
export function parseModerationSuccess(
  res: ProfileSettingsSaveResponse | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): ProfileModerationBlock | null {
  const mod = res?.moderation;
  if (!mod || mod.escalated !== true) return null;
  const i18nKey = typeof mod.i18nKey === "string" ? mod.i18nKey : null;
  const serverMessage = typeof mod.message === "string" ? mod.message : "";
  const localized = i18nKey ? t(i18nKey) : "";
  const message = localized && localized !== i18nKey ? localized : serverMessage;
  return { message, escalated: true, attemptsRemaining: null };
}

// Task #2833 (Flow Review 05, Finding C) — consumes the confirmation
// link emailed to the host's payout_email override. Public endpoint
// (no auth header) — security rests on the unguessable 32-byte token.
// Throws an ApiError with `.code` set to one of: invalid_token,
// token_not_found, token_expired, lookup_failed, update_failed,
// database_unavailable.
export async function verifyPayoutEmail(token: string): Promise<{ success: boolean; payout_email: string | null }> {
  const res = await fetch(`${API_BASE}/users/payout-email/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(body.error || `API error ${res.status}`, res.status, body);
  }
  return body as { success: boolean; payout_email: string | null };
}

export function resendVerificationEmail(token: string) {
  return authFetch<{ sent: boolean; already_verified: boolean }>("/auth/resend-verification", token, {
    method: "POST",
  });
}

export async function changeEmail(token: string, email: string) {
  // Sensitive — gated by `requireStepUp("change_email")`. withStepUp
  // intercepts the 401 envelope, pops the re-auth modal, and retries.
  const { withStepUp } = await import("./step-up");
  const res = await withStepUp(() =>
    fetch(`${API_BASE}/auth/change-email`, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ email }),
    }),
  );
  if (!res.ok) {
    let body: Record<string, unknown> | undefined;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new ApiError(
      (body?.message as string) || (body?.error as string) || `Request failed (${res.status})`,
      res.status,
      body,
    );
  }
  return res.json() as Promise<{ ok: boolean; email: string; verification_email_sent: boolean; unchanged?: boolean }>;
}

export interface UserSession {
  id: string;
  ip: string | null;
  os: string;
  browser: string;
  deviceType: "mobile" | "desktop";
  created_at: string;
  last_active_at: string;
  is_current: boolean;
}

export function fetchUserSessions(token: string) {
  return authFetch<{ sessions: UserSession[] }>("/users/me/sessions", token);
}

export async function changePassword(token: string, currentPassword: string, newPassword: string) {
  // Note: change-password already verifies the current password
  // server-side, so it BOTH satisfies and stamps step-up on success.
  // We still route through withStepUp so a stale stamp on a follow-on
  // call (e.g. revoke-other-sessions) inherits the fresh confirmation.
  const { withStepUp } = await import("./step-up");
  const res = await withStepUp(() =>
    fetch(`${API_BASE}/users/me/change-password`, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  );
  if (!res.ok) {
    let body: Record<string, unknown> | undefined;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new ApiError(
      (body?.message as string) || (body?.error as string) || `Request failed (${res.status})`,
      res.status,
      body,
    );
  }
  return res.json() as Promise<{ success: boolean }>;
}

/** Whether the signed-in user's auth account has a password set. False
 * for OTP-only signups; the Settings page switches to "Set a password"
 * (no current-password challenge) in that case. */
export function getHasPassword(token: string) {
  return authFetch<{ has_password: boolean }>("/users/me/has-password", token);
}

/** Sets the first password on an OTP-only account. The server refuses
 * with 409 `password_already_set` if a password is present — those
 * callers must use `changePassword` and prove the current credential. */
export function setInitialPassword(token: string, newPassword: string) {
  return authFetch<{ success: boolean }>("/users/me/set-initial-password", token, {
    method: "POST",
    body: JSON.stringify({ newPassword }),
  });
}

/** Public bypass for the Supabase /otp rate limit. Triggers a magic-link
 * email through our own Resend transport. Anti-enumeration: returns
 * { ok: true } regardless of whether the address exists. Subject to our
 * own per-email (3/h) + per-IP (10/h) limits server-side. */
export async function sendMagicLinkBypass(email: string, next?: string): Promise<{ ok: boolean; rateLimited?: boolean }> {
  const apiBase = (typeof window !== "undefined" && (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL) || "/";
  const turnstileToken = await getTurnstileToken();
  const res = await fetch(`${apiBase}api/auth/send-magic-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, next, [TURNSTILE_HEADER]: turnstileToken ?? undefined }),
  });
  if (res.status === 429) return { ok: false, rateLimited: true };
  if (!res.ok) return { ok: false };
  return { ok: true };
}

export function revokeUserSession(token: string, sessionId: string) {
  return authFetch<{ success: boolean }>(`/users/me/sessions/${sessionId}/revoke`, token, {
    method: "POST",
  });
}

/** Task #3064 — Sign out every device except the one calling this. */
export function revokeOtherUserSessions(token: string) {
  /**
   * "Sign out other devices" (Task #3061). Server uses the JWT
   * `session_id` claim to exclude the caller, so this is safe to invoke
   * from the current session without logging the user out of the tab
   * that initiated the request.
   */
  return authFetch<{ success: boolean; revoked: number }>(
    "/users/me/sessions/revoke-others",
    token,
    { method: "POST" },
  );
}

/** Task #3064 — Recent security activity for the Settings → Security panel. */
export interface AuthEvent {
  id: string;
  event_type:
    | "sign_in"
    | "sign_out"
    | "password_change"
    | "mfa_enroll"
    | "mfa_unenroll"
    | "session_revoked"
    | "sessions_revoked_others"
    | "new_device_email_sent";
  ip: string | null;
  os: string;
  browser: string;
  deviceType: "mobile" | "desktop";
  created_at: string;
  metadata: Record<string, unknown>;
}

export function fetchAuthEvents(token: string) {
  return authFetch<{ events: AuthEvent[] }>("/users/me/auth-events", token);
}

/**
 * Task #3064 — Log a client-side MFA event for the activity panel.
 *
 * MFA enroll/unenroll runs browser → Supabase directly, so the
 * server can't observe it from a route. The client posts here right
 * after the Supabase call succeeds.
 */
export function recordMfaEvent(token: string, kind: "enroll" | "unenroll") {
  return authFetch<{ success: boolean }>("/users/me/auth-events/mfa", token, {
    method: "POST",
    body: JSON.stringify({ kind }),
  });
}

export function updateProfilePhoto(token: string, url: string, storage?: string) {
  return authFetch<{ success: boolean; profileImageUrl?: string }>("/users/me/profile-photo", token, {
    method: "POST",
    body: JSON.stringify({ url, storage }),
  });
}

export function deleteProfilePhoto(token: string) {
  return authFetch<{ success: boolean }>("/users/me/profile-photo", token, {
    method: "DELETE",
  });
}

export function requestCoverImageUploadUrl(token: string, contentType: string) {
  return authFetch<{ uploadURL: string; objectPath: string; storage: string }>("/users/me/cover-image/request-upload-url", token, {
    method: "POST",
    body: JSON.stringify({ contentType }),
  });
}

export function updateCoverImage(token: string, url: string, storage?: string) {
  return authFetch<{ success: boolean; coverImageUrl?: string }>("/users/me/cover-image", token, {
    method: "POST",
    body: JSON.stringify({ url, storage }),
  });
}

// Direct (server-side) cover image upload. Replaces the old
// presigned-URL + browser-PUT flow which 100% failed with
// "Failed to fetch" because the R2 bucket backing covers had no
// CORS rule for the site origin (vehicle-photos uploads avoid the
// same trap by streaming through the api-server). Mirrors
// uploadProfilePhoto.
export async function uploadCoverImage(
  token: string,
  blob: Blob,
): Promise<{ success: boolean; coverImageUrl: string; objectPath: string; storage: string }> {
  const contentType = blob.type || "image/jpeg";
  const res = await fetch(`${API_BASE}/users/me/cover-image/upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": contentType,
    },
    body: blob,
  });
  if (!res.ok) {
    let message = `Cover upload failed (${res.status})`;
    try {
      const j = await res.json();
      message = j.error || j.message || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

export function enrichProfileField(token: string, field: string, currentValue: string) {
  return authFetch<{ text: string; field: string }>("/users/me/enrich-profile", token, {
    method: "POST",
    body: JSON.stringify({ field, currentValue }),
  });
}

export async function uploadProfilePhoto(token: string, file: File): Promise<{ profileImageUrl: string; objectPath: string; storage: string }> {
  const res = await fetch(`${API_BASE}/users/me/profile-photo/upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type,
    },
    body: file,
  });
  if (!res.ok) {
    let message = `Photo upload failed (${res.status})`;
    try {
      const j = await res.json();
      message = j.error || j.message || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

export async function deleteAccount(token: string): Promise<{ success: boolean; scheduledErasureAt?: string }> {
  // Sensitive — server gates with `requireStepUp("delete_account")`.
  const { withStepUp } = await import("./step-up");
  const res = await withStepUp(() => fetch(`${API_BASE}/users/me/account`, {
    method: "DELETE",
    body: JSON.stringify({ confirmListingsRemoved: true }),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }));
  if (!res.ok) {
    let body: Record<string, unknown> | undefined;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new ApiError(
      (body?.message as string) || (body?.error as string) || `Request failed (${res.status})`,
      res.status,
      body,
    );
  }
  return res.json();
}

export async function requestDataExport(token: string): Promise<Blob> {
  const { withStepUp } = await import("./step-up");
  const res = await withStepUp(() =>
    fetch(`${API_BASE}/users/me/data-export`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }),
  );
  if (!res.ok) {
    let message = `Export failed (${res.status})`;
    try { const j = await res.json(); message = j.error || j.message || message; } catch {}
    throw new Error(message);
  }
  return await res.blob();
}

export interface FavouriteCar {
  id: number;
  make: string;
  model: string;
  year: number;
  slug: string;
  price_driving: number | null;
  /** Task #2360: the full price-column set the home `CarCard` walks
   *  through when picking a "from £X / day" string. The api-server
   *  `/users/me/favourites` endpoint now returns all of these so the
   *  Favourites page never falls back to "Price on request" for a
   *  listing that shows a real price on the home page. */
  price_event?: number | null;
  minimum_price?: number | null;
  chauffeur_min_price?: number | null;
  event_min_price?: number | null;
  /** Task #(car-1059383): rental-type active flags so the favourites
   *  tile's `getDisplayPrice` skips prices in disabled rental columns
   *  (a host can disable event hire but leave `price_event` populated,
   *  or a keysmash typo can land in an inactive column). Undefined on
   *  older API responses — `getDisplayPrice` falls back to showing
   *  nothing rather than the wrong rental's price. */
  rental_driving_active?: boolean | null;
  rental_chauffeured_active?: boolean | null;
  rental_event_active?: boolean | null;
  /** ISO currency for `price_driving`. The api-server
   *  `/users/me/favourites` endpoint returns this as the listing's
   *  `cars.currency_code`. `local_currency_code` is kept as a
   *  back-compat alias for older callers and may be undefined on the
   *  current payload. UI prefers `currency_code` and falls back to
   *  `local_currency_code` then "USD". */
  currency_code?: string | null;
  local_currency_code?: string | null;
  city: string;
  state: string;
  listing_status: string;
  primary_image: string | null;
  favourite_id: number;
  /** Task #2340: api-server `/users/me/favourites` now returns this flag
   *  (true when listing_status === "active"). Used to overlay an
   *  "Unavailable" badge on paused/inactive tiles while keeping them
   *  clickable, mirroring the mobile Wishlists screen. */
  is_available?: boolean;
}

export function fetchFavourites(token: string) {
  return authFetch<FavouriteCar[]>("/users/me/favourites", token);
}

export interface NearbyCar {
  id: number;
  slug: string;
  url: string;
  make: string;
  model: string;
  year: number;
  city: string;
  state: string;
  country_code: string | null;
  distance_km: number;
  price_per_day: number | null;
  currency_code: string;
  image_url: string | null;
  passengers: number | null;
  transmission: string | null;
}

export interface NearbyCarsResponse {
  cars: NearbyCar[];
  count: number;
  search_center: { lat: number; lng: number };
  radius_km: number;
}

/** Public, no-auth `GET /api/public/cars/nearby` wrapper used by the web
 *  Favourites page (task #2340) to populate the "Local cars near you"
 *  rail. Server returns the nearest-active cars sorted by distance. */
export async function fetchNearbyCars(opts: {
  lat: number;
  lng: number;
  radiusKm?: number;
  limit?: number;
  countryCode?: string | null;
}): Promise<NearbyCarsResponse> {
  const sp = new URLSearchParams({
    lat: String(opts.lat),
    lng: String(opts.lng),
    radius_km: String(opts.radiusKm ?? 150),
    limit: String(opts.limit ?? 12),
  });
  if (opts.countryCode) sp.set("country_code", opts.countryCode);
  const res = await fetch(`${API_BASE}/public/cars/nearby?${sp.toString()}`);
  if (!res.ok) throw new Error(`nearby cars: ${res.status}`);
  return (await res.json()) as NearbyCarsResponse;
}

export function toggleFavourite(token: string, carId: number) {
  return authFetch<{ favourited: boolean }>("/users/me/favourites/toggle", token, {
    method: "POST",
    body: JSON.stringify({ car_id: carId }),
  });
}

export function fetchFavouriteIds(token: string) {
  return authFetch<number[]>("/users/me/favourites/ids", token);
}

export interface ReviewCategoryRating {
  category: string;
  rating: number;
}

export interface Review {
  id: number;
  rating: number;
  reviewText: string | null;
  tripId: number;
  carId: number;
  createdAt: string;
  reviewer?: { first_name: string | null; last_name: string | null; profile_image: string | null } | null;
  /** Author display label; distinguishes anonymous imports from deleted accounts. */
  reviewerName?: string;
  reviewee?: { first_name: string | null; last_name: string | null; profile_image: string | null } | null;
  car?: { make: string; model: string; year: number; primary_image_url?: string | null } | null;
  images?: string[];
  /** Per-category 1-5 ratings (empty for legacy reviews). */
  categoryRatings?: ReviewCategoryRating[];
  /** Curated highlight chip slugs. */
  tags?: string[];
}

export interface PendingReview {
  trip_id: number;
  trip_public_id: string;
  reviewee_id: string;
  car_id: number;
  end_date: string;
  reviewee: { first_name: string; last_name: string; profile_image: string | null } | null;
  car: { make: string; model: string; year: number; primary_image_url?: string | null } | null;
  /** "guest_to_car" when current user is the renter, "host_to_guest" when the owner. */
  direction: "guest_to_car" | "host_to_guest";
}

export function fetchReviews(token: string, tab: string) {
  return authFetch<Review[] | PendingReview[]>(`/users/me/reviews?tab=${tab}`, token);
}

export interface CreateReviewResult {
  id: number;
  /** False while the review is held under the double-blind gate (the other
   *  party has not yet reviewed and the reveal deadline has not passed). */
  is_published: boolean;
  /** ISO-8601 timestamp after which the cron will auto-publish, or null if
   *  the review was published immediately (both parties reviewed). */
  reveal_deadline: string | null;
}

export function createReview(
  token: string,
  tripId: number,
  rating: number,
  reviewText: string,
  imageUrl?: string | null,
  opts?: { categoryRatings?: ReviewCategoryRating[]; tags?: string[] },
) {
  return authFetch<CreateReviewResult>("/users/me/reviews", token, {
    method: "POST",
    body: JSON.stringify({
      trip_id: tripId,
      rating,
      review_text: reviewText,
      image_url: imageUrl ?? undefined,
      categoryRatings: opts?.categoryRatings,
      tags: opts?.tags,
    }),
  });
}

// Upload a single review photo. The server returns the public CDN URL,
// which must be passed back through createReview() so the photo gets
// linked to the review row in trip_review_images.
export async function uploadReviewImage(token: string, file: File): Promise<{ url: string }> {
  const res = await fetch(`${API_BASE}/users/me/reviews/upload-image`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type,
    },
    body: file,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try { const j = await res.json(); message = j.error || message; } catch {}
    throw new Error(message);
  }
  return res.json();
}

export interface Notification {
  id: number;
  type: string;
  message: string;
  is_read: boolean;
  action_url: string | null;
  created_at: string;
  data?: Record<string, unknown> | null;
}

export function fetchNotifications(
  token: string,
  opts?: { before?: string | null; beforeId?: number | null; limit?: number },
) {
  const sp = new URLSearchParams();
  if (opts?.before) sp.set("before", opts.before);
  if (opts?.beforeId != null) sp.set("before_id", String(opts.beforeId));
  if (opts?.limit) sp.set("limit", String(opts.limit));
  const qs = sp.toString();
  return authFetch<Notification[]>(
    `/users/me/notifications${qs ? `?${qs}` : ""}`,
    token,
  );
}

export function markNotificationsRead(token: string, ids?: number[]) {
  return authFetch<{ success: boolean }>("/users/me/notifications/mark-read", token, {
    method: "POST",
    body: JSON.stringify({ ids }),
  }).then((res) => {
    // Task #2425 — ping the header so its unread badge recounts even if
    // the Supabase realtime channel isn't connected.
    emitNotificationsChanged();
    return res;
  });
}

export function deleteNotification(token: string, id: number) {
  return authFetch<{ success: boolean }>(`/users/me/notifications/${id}`, token, {
    method: "DELETE",
  }).then((res) => {
    // Task #2425 — see markNotificationsRead above.
    emitNotificationsChanged();
    return res;
  });
}

export function fetchUnreadNotificationCount(token: string) {
  return authFetch<{ count: number }>("/users/me/notifications/unread-count", token);
}

export interface DashboardOverview {
  upcoming_trips: Array<{
    id: number;
    public_id: string;
    car_id: number;
    started_at: string | null;
    pick_up?: string | null;
    drop_off: string | null;
    /** Optional for compatibility with older overview responses. */
    booking_type?: string | null;
    status?: string | null;
    times_captured?: boolean | null;
    car: { make: string; model: string; year: number; slug: string } | null;
    renter?: {
      id?: number;
      first_name: string | null;
      last_name: string | null;
      profile_image?: string | null;
    } | null;
    /** Task #2394 — true when this booking has a pending guest-initiated
     *  proposal (price/dates/location adjustment) waiting on the host. */
    pending_guest_proposal?: boolean;
    pending_guest_proposal_id?: number | null;
  }>;
  unread_messages: number;
  pending_bookings: number;
  /** Task #2394 — total count of the host's active bookings that have
   *  a pending guest-initiated proposal. Spans every active booking,
   *  not just the ones returned in `upcoming_trips`. */
  pending_guest_proposals?: number;
  /** Task #2394 — when exactly one booking has a pending guest
   *  proposal, the public_id of that trip so the dashboard banner CTA
   *  can deep-link straight to it. Null otherwise. */
  pending_guest_proposal_trip_public_id?: string | null;
  /** Task #2921 — when exactly one booking has a pending guest
   *  proposal, the conversation public_id between host and renter for
   *  that trip's car. Used to deep-link the banner into the chat with
   *  the change-request UI surfaced, matching the convention used by
   *  OwnerBookingDetailPage / TripDetailPage. Null otherwise (or when
   *  no conversation exists yet). */
  pending_guest_proposal_conversation_public_id?: string | null;
  /** Task #2921 — proposal (trip_suggestion) id of the single pending
   *  guest-initiated proposal. Used as the `#proposal-<id>` anchor on
   *  the chat deep-link so the right card scrolls into view. */
  pending_guest_proposal_id?: number | null;
  unread_notifications: number;
  verification: {
    email: boolean;
    phone: boolean;
    renter: boolean;
  };
}

export function fetchDashboardOverview(token: string) {
  return authFetch<DashboardOverview>("/users/me/dashboard-overview", token);
}

// Task #923 — host-dashboard payout-failure banner.
export interface PayoutFailureStatus {
  reason: string;
  failedAt: string | null;
  payoutId: string | null;
  reonboardUrl: string;
}
export interface PayoutStatusResponse {
  payoutFailure: PayoutFailureStatus | null;
}
export function fetchPayoutStatus(token: string) {
  return authFetch<PayoutStatusResponse>("/users/me/payout-status", token);
}

export interface VerificationStatus {
  email_verified: boolean;
  phone_verified: boolean;
  phone: string | null;
  renter_verified: boolean;
  /**
   * Canonical driver licence verification status. Always read this in the UI
   * instead of combining the legacy `licence_pending` flag and
   * `driver.license_verification_status` numeric column.
   */
  license_status: "none" | "pending" | "verified" | "rejected" | "expired";
  /** @deprecated Use `license_status`. Kept for one release for compatibility. */
  licence_pending: boolean;
  /** A licence re-upload request is open or has not yet produced a Didit attempt. */
  licence_reupload_requested?: boolean;
  /** @deprecated Use `license_status`. Kept for one release for compatibility. */
  driver: {
    id: number;
    license_verification_status: number;
    is_active: boolean;
    created_at: string;
  } | null;
}

export function fetchVerificationStatus(token: string) {
  return authFetch<VerificationStatus>("/users/me/verification", token);
}

export interface BookingVerificationRequirements {
  requires_email: boolean;
  requires_phone: boolean;
}

export type RentalKind = "driving" | "chauffeured" | "event";

export type BookingVerificationMatrix = Record<RentalKind, BookingVerificationRequirements>;

export async function fetchBookingVerificationRequirements(rentalType: RentalKind): Promise<BookingVerificationRequirements> {
  const res = await fetch(`${API_BASE}/config/booking-verification-requirements?rental_type=${encodeURIComponent(rentalType)}`);
  if (!res.ok) throw new Error(`Failed to load verification requirements (${res.status})`);
  const body = await res.json();
  return body.requirements as BookingVerificationRequirements;
}

export async function fetchBookingVerificationMatrix(): Promise<BookingVerificationMatrix> {
  const res = await fetch(`${API_BASE}/config/booking-verification-requirements`);
  if (!res.ok) throw new Error(`Failed to load verification matrix (${res.status})`);
  const body = await res.json();
  return body.matrix as BookingVerificationMatrix;
}

/**
 * Host-side publish-readiness requirements (task #1007). Used by the
 * listing wizard's review/submit step to decide whether to render the
 * inline phone-verification gate. Defaults to `requires_phone: true` if
 * the public config endpoint is unreachable so the UI fails closed —
 * the server-side gate enforces the same default.
 */
export interface HostPublishRequirements {
  requires_phone: boolean;
}

export async function fetchHostPublishRequirements(): Promise<HostPublishRequirements> {
  try {
    const res = await fetch(`${API_BASE}/config/host-publish-requirements`);
    if (!res.ok) return { requires_phone: true };
    const body = await res.json();
    const r = (body?.requirements ?? {}) as Partial<HostPublishRequirements>;
    return { requires_phone: r.requires_phone !== false };
  } catch {
    return { requires_phone: true };
  }
}

export interface SendPhoneVerificationOptions {
  channel?: "sms" | "call";
  locale?: string;
}

export function sendPhoneVerification(
  token: string,
  phone: string,
  options: SendPhoneVerificationOptions = {},
) {
  const body: Record<string, unknown> = { phone };
  if (options.channel) body.channel = options.channel;
  if (options.locale) body.locale = options.locale;
  return authFetch<{ success: boolean; demo?: boolean; message?: string }>("/users/me/verify-phone/send", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function checkPhoneVerification(token: string, phone: string, code: string) {
  return authFetch<{ success: boolean; demo?: boolean }>("/users/me/verify-phone/check", token, {
    method: "POST",
    body: JSON.stringify({ phone, code }),
  });
}

export function startLicenceVerification(token: string, publicId?: string) {
  return authFetch<{ success: boolean; verification_url: string | null; verification_qr: string | null; session_id: string | null; preparing?: boolean; session_is_current?: boolean }>("/users/me/verify-licence", token, {
    method: "POST",
    body: JSON.stringify(publicId ? { trip_public_id: publicId } : {}),
  });
}

// Task #866: lightweight polling endpoint backing the in-app verification
// screen. The server may optionally provide polling guidance; older servers
// omit these fields and the web client falls back to a bounded cadence.
export type LicenceVerificationPhase =
  | "not_started"
  | "didit_in_progress"
  | "didit_in_review"
  | "didit_declined"
  | "mvr_in_progress"
  | "mvr_review"
  | "mvr_declined"
  | "approved"
  | "cancelled"
  | "error"
  | "manual_review"
  | "stale";

export interface LicenceVerificationStatus {
  phase: LicenceVerificationPhase;
  message: string | null;
  /** The previously returned provider session was deliberately retired. */
  retired_attempt?: boolean;
  /** True only while the server still authorizes reuse of this exact Didit session. */
  session_is_current?: boolean;
  review_reason: string | null;
  review_decision: string | null;
  licence_verified: boolean;
  mvr_status: string | null;
  mvr_completed_at: string | null;
  last_session_id: string | null;
  kyc_log: { status: string; reason: string | null; verified_at: string | null; created_at: string } | null;
  /** Optional server-owned refresh guidance (introduced after the original endpoint). */
  next_poll_at?: string | null;
  nextPollAt?: string | null;
  poll_after_ms?: number | null;
  pollAfterMs?: number | null;
  last_updated_at?: string | null;
  lastUpdatedAt?: string | null;
  ui_state?: "idle" | "processing" | "delayed" | "stale" | "error" | "manual_review" | "cancelled" | "terminal" | null;
  retryable?: boolean;
  terminal?: boolean;
  should_poll?: boolean;
  attempt?: number | null;
  attempts_used?: number | null;
  attempts_remaining?: number | null;
  max_attempts?: number | null;
  context_id?: number | null;
  assignment_id?: number | null;
  subject?: string | null;
  provider?: LicenceVerificationCanonicalStatus["provider"];
  timestamps?: LicenceVerificationCanonicalStatus["timestamps"];
  status?: LicenceVerificationCanonicalStatus;
}

export interface LicenceVerificationCanonicalStatus {
  phase?: LicenceVerificationPhase;
  ui_state?: "idle" | "processing" | "delayed" | "error" | "manual_review" | "terminal" | "stale";
  message?: string | null;
  terminal?: boolean;
  should_poll?: boolean;
  retryable?: boolean;
  delayed?: boolean;
  stale?: boolean;
  error?: boolean;
  manual_review?: boolean;
  next_poll_ms?: number | null;
  next_poll_at?: string | null;
  attempt?: number | null;
  attempts_used?: number | null;
  attempts_remaining?: number | null;
  max_attempts?: number | null;
  context_id?: number | null;
  assignment_id?: number | null;
  subject?: string | null;
  age_ms?: number | null;
  provider?: {
    didit_session_id?: string | null;
    provider_ref?: string | null;
    verification_url?: string | null;
    verification_qr?: string | null;
    session_is_current?: boolean;
  };
  timestamps?: {
    started_at?: string | null;
    updated_at?: string | null;
    verified_at?: string | null;
    completed_at?: string | null;
  };
}

type LicenceVerificationResponse = Omit<LicenceVerificationStatus, "status"> & {
  status?: LicenceVerificationCanonicalStatus;
};

/** Normalize the canonical nested response while retaining compatibility with
 * older deployments that returned the legacy fields at the top level. */
export async function fetchLicenceVerificationStatus(token: string, publicId?: string): Promise<LicenceVerificationStatus> {
  const query = publicId ? `?trip_public_id=${encodeURIComponent(publicId)}` : "";
  const response = await authFetch<LicenceVerificationResponse>(`/users/me/verify-licence/status${query}`, token);
  const canonical = response.status;
  if (!canonical) return response;
  return {
    ...response,
    phase: canonical.phase ?? response.phase,
    message: canonical.message ?? response.message,
    ui_state: canonical.ui_state ?? response.ui_state,
    retryable: canonical.retryable ?? response.retryable,
    attempts_used: canonical.attempts_used ?? response.attempts_used ?? null,
    attempts_remaining: canonical.attempts_remaining ?? response.attempts_remaining ?? null,
    max_attempts: canonical.max_attempts ?? response.max_attempts ?? null,
    terminal: canonical.terminal ?? response.terminal,
    should_poll: canonical.should_poll ?? response.should_poll,
    poll_after_ms: canonical.next_poll_ms ?? response.poll_after_ms ?? null,
    next_poll_at: canonical.next_poll_at ?? response.next_poll_at ?? null,
    last_updated_at: canonical.timestamps?.updated_at ?? response.last_updated_at ?? null,
    lastUpdatedAt: canonical.timestamps?.updated_at ?? response.lastUpdatedAt ?? null,
    attempt: canonical.attempt ?? response.attempt,
    context_id: canonical.context_id ?? response.context_id,
    assignment_id: canonical.assignment_id ?? response.assignment_id,
    subject: canonical.subject ?? response.subject,
    provider: canonical.provider ?? response.provider,
    timestamps: canonical.timestamps ?? response.timestamps,
  };
}

export async function requestDocumentUploadUrl(token: string, contentType: string) {
  return authFetch<{ uploadURL: string; objectPath: string; storage: string }>("/users/me/documents/request-upload-url", token, {
    method: "POST",
    body: JSON.stringify({ contentType }),
  });
}

export async function uploadDocument(token: string, file: File): Promise<{ objectPath: string; storage: string }> {
  const { uploadURL, objectPath, storage } = await requestDocumentUploadUrl(token, file.type);
  const putRes = await fetch(uploadURL, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  if (!putRes.ok) {
    throw new Error(`Document upload failed (${putRes.status})`);
  }
  return { objectPath, storage };
}

export interface PublicProfile {
  /** Public surname fields contain an initial only, never a legal surname. */
  display_name?: string;
  collectionCanonicalPath?: string;
  id: string;
  first_name: string;
  last_name: string;
  about: string | null;
  quick_intro: string | null;
  city: string | null;
  state: string | null;
  country_code: string | null;
  profile_image: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  renter_verified: boolean;
  created_at: string;
  pro_owner_tier?: string | null;
  host_bio: string | null;
  car_story: string | null;
  auto_experience: string | null;
  specializations: string[] | null;
  certifications: string[] | null;
  cover_image: string | null;
  host_profile_moderation_status: string | null;
  cars: Array<{ id: number; make: string; model: string; year: number; slug: string; price_driving: number; local_currency_code?: string | null; city: string; state: string; primaryImageUrl: string | null }>;
  reviews: Array<Omit<Review, "rating"> & { rating: number | null }>;
  reviewSummary: import("./api").ReviewSummary | null;
  // Headline review stats over the FULL received-review population (legacy
  // + bidirectional), not just the surfaced reviews. May be absent on
  // responses served before this field shipped.
  reviewStats?: HostReviewStats | null;
}

export interface HostReviewStats {
  averageRating: number | null;
  reviewCount: number;
}

export function fetchPublicProfile(userId: string, options: { allReviews?: boolean } = {}) {
  return fetch(`${API_BASE}/users/profile/${userId}${options.allReviews ? "?all_reviews=1" : ""}`).then(r => {
    if (!r.ok) throw new Error(r.status === 404 ? "User not found" : "Public profile temporarily unavailable. Please try again.");
    return r.json() as Promise<PublicProfile>;
  });
}

/**
 * Resolve a generated driveshare collection URL to its migrated public
 * profile. The API intentionally owns the source_id/platform-origin mapping;
 * callers must not guess that a legacy id is a current users.id.
 */
export async function fetchLegacyCollectionProfile(sourceId: string, country?: string): Promise<PublicProfile> {
  const response = await fetch(`${API_BASE}/users/legacy-collection/${encodeURIComponent(sourceId)}${country ? `?country=${encodeURIComponent(country)}` : ""}`);
  if (!response.ok) {
    throw new Error(response.status === 404 ? "Collection not found" : "Collection temporarily unavailable");
  }
  const mapping = await response.json() as { userId?: unknown; carIds?: unknown; canonicalPath?: string; identityKind?: string };
  if (typeof mapping.userId !== "number" || !Number.isSafeInteger(mapping.userId) || mapping.userId < 1) {
    throw new Error("Collection profile mapping is invalid");
  }
  if (!Array.isArray(mapping.carIds) || mapping.carIds.some((id) => typeof id !== "number" || !Number.isSafeInteger(id) || id < 1)) {
    throw new Error("Collection inventory mapping is invalid");
  }
  const profile = await fetchPublicProfile(String(mapping.userId), { allReviews: true });
  if (String(profile.id) !== String(mapping.userId)) {
    throw new Error("Collection profile mapping is invalid");
  }
  if (mapping.identityKind === "bac" && country && profile.country_code?.toUpperCase() !== country) {
    throw new Error("Collection profile mapping is invalid");
  }
  const legacyCarIds = new Set(mapping.carIds as number[]);
  const publicIds = new Set(profile.cars.map(car => car.id));
  if (legacyCarIds.size !== mapping.carIds.length || [...legacyCarIds].some(id => !publicIds.has(id))) {
    // Ownership/publication can change between the two required reads. Never
    // silently render a partial fleet as successful collection acceptance.
    throw new Error("Collection inventory changed. Please try again.");
  }
  return { ...profile, collectionCanonicalPath: mapping.canonicalPath,
    cars: profile.cars.filter((car) => legacyCarIds.has(car.id)) };
}
export interface CalendarFeedPerCar {
  carId: number;
  label: string;
  feedUrl: string;
  webcalUrl: string;
}

export interface CalendarFeedStatus {
  active: boolean;
  token: string | null;
  feedUrl: string | null;
  webcalUrl?: string | null;
  perCarFeedUrls: CalendarFeedPerCar[];
}

export function fetchCalendarFeedStatus(token: string): Promise<CalendarFeedStatus> {
  return authFetch<CalendarFeedStatus>("/calendar/feed-status", token);
}

export function generateCalendarFeedToken(token: string): Promise<{ token: string; feedUrl: string; webcalUrl: string }> {
  return authFetch("/calendar/feed-token", token, { method: "POST" });
}

export function revokeCalendarFeedToken(token: string): Promise<{ ok: boolean }> {
  return authFetch("/calendar/feed-token", token, { method: "DELETE" });
}
